import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../services/api';

const CareerChavdiSection = ({ customPadding, hideSubtitle = false, isStandalone = false }) => {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    // ... useEffect and other logic ...
    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await api.get('/career-chavdi');
                if (response.success && response.data) {
                    setContent(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch Career Chavdi content", error);
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, []);

    // Fallbacks if data fails to load or hasn't loaded yet
    const displayData = content || {
        subtitle: "Discover & Learn",
        title: "Empowering Your Professional Journey",
        description: "Welcome to Career Chavdi, your ultimate destination for career guidance and professional growth. Our expert-led sessions and comprehensive resources are designed to help you navigate your career path with confidence.",
        points: [
            "Expert Career Counseling",
            "Industry Insights & Trends",
            "Resume Building Workshops",
            "Interview Preparation Strategies"
        ],
        videoUrl: "https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
        quote: '"Education is not the learning of facts, but the training of the mind to think."'
    };

    if (loading) {
        // Prevent flickering a default state by returning null or a skeleton here if preferred. 
        // For now, returning the section with default data while loading is also acceptable.
    }

    return (
        <div className={`${customPadding || 'pt-10 pb-20 lg:pt-10 lg:pb-32'} bg-brand-bg relative overflow-hidden`}>
            <div className="max-w-7xl mx-auto px-6 relative z-10">
                {/* Section Header */}
                <div className="mb-12 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        {!hideSubtitle && (
                            <span className="inline-block px-4 py-1.5 rounded-full bg-red-50 text-brand-gold font-semibold text-sm mb-4 border border-red-100">
                                {displayData.subtitle}
                            </span>
                        )}
                        <h2 className="text-3xl font-bold text-brand-textMuted">
                            Career <span className="text-brand-gold">Chavdi</span>
                        </h2>
                        {/* <div className="w-24 h-1 bg-brand-gold mt-2 rounded-full mx-auto"></div>  */}
                    </motion.div>
                </div>

                {/* Content Area */}
                <div className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-16`}>

                    {/* Left Side: Text Information */}
                    <div className={`w-full ${isStandalone ? 'lg:w-1/2' : 'lg:w-2/5'}`}>
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.8 }}
                        >
                            <h3 className="text-3xl md:text-4xl font-bold text-brand-textLight mb-6 leading-tight">
                                {displayData.title}
                            </h3>
                            <p className="text-lg text-brand-textMuted leading-relaxed whitespace-pre-wrap mb-8">
                                {displayData.description}
                            </p>
                            <ul className="space-y-4 mb-10">
                                {displayData.points.map((item, index) => (
                                    <motion.li
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.2 + (index * 0.1) }}
                                        className="flex items-center text-brand-textLight"
                                    >
                                        <div className="w-2 h-2 bg-brand-gold rounded-full mr-3 shrink-0"></div>
                                        <span className="font-medium">{item}</span>
                                    </motion.li>
                                ))}
                            </ul>

                            {/* "Read More" button only shown on the homepage (not standalone) */}
                            {!isStandalone && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.6 }}
                                    className="mt-8"
                                >
                                    <Link
                                        to="/career-chavdi"
                                        className="inline-flex items-center px-6 py-3 bg-brand-gold text-black font-medium rounded-full shadow-lg shadow-[#E91E63]/30 hover:bg-[#c2185b] hover:shadow-[#E91E63]/50 transition-all duration-300 transform hover:-translate-y-1"
                                    >
                                        Read More
                                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </Link>
                                </motion.div>
                            )}
                        </motion.div>
                    </div>

                    {/* Right Side: Video */}
                    <div className={`w-full ${isStandalone ? 'lg:w-1/2 lg:pl-10' : 'lg:w-3/5 lg:pl-16'} relative mt-12 lg:mt-0`}>
                        <div className={`relative w-full ${isStandalone ? 'h-[250px] sm:h-[350px] lg:h-[450px]' : 'h-[250px] sm:h-[300px] lg:h-[350px]'}`}>

                            {/* Decorative Background Offset Layers */}
                            <div className="absolute -top-4 -right-4 lg:-top-6 lg:-right-6 w-full h-full bg-[#f3e8ff] rounded-3xl z-0"></div>
                            <div className="absolute -bottom-4 -left-4 lg:-bottom-6 lg:-left-6 w-full h-full bg-[#e0f2fe] rounded-3xl z-0"></div>

                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.8 }}
                                className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl bg-slate-200 border-4 border-white z-10 flex flex-col justify-end"
                            >
                                {displayData.videoUrl && (displayData.videoUrl.includes('youtube.com') || displayData.videoUrl.includes('youtu.be')) ? (
                                    <iframe
                                        key={displayData.videoUrl}
                                        className="absolute inset-0 w-full h-full object-cover z-0"
                                        src={
                                            displayData.videoUrl.includes('youtube.com/watch?v=')
                                                ? displayData.videoUrl.replace('watch?v=', 'embed/') + '?autoplay=1&mute=1&loop=1&playlist=' + displayData.videoUrl.split('v=')[1]?.split('&')[0]
                                                : displayData.videoUrl.includes('youtu.be/')
                                                    ? displayData.videoUrl.replace('youtu.be/', 'youtube.com/embed/') + '?autoplay=1&mute=1&loop=1&playlist=' + displayData.videoUrl.split('youtu.be/')[1]?.split('?')[0]
                                                    : displayData.videoUrl.includes('youtube.com/shorts/')
                                                        ? displayData.videoUrl.replace('youtube.com/shorts/', 'youtube.com/embed/') + '?autoplay=1&mute=1&loop=1&playlist=' + displayData.videoUrl.split('shorts/')[1]?.split('?')[0]
                                                        : displayData.videoUrl
                                        }
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                    ></iframe>
                                ) : (
                                    <video
                                        key={displayData.videoUrl}
                                        className="absolute inset-0 w-full h-full object-cover z-0"
                                        controls
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                    >
                                        <source src={displayData.videoUrl} type="video/mp4" />
                                        Your browser does not support HTML video.
                                    </video>
                                )}

                                {/* Quote overlay */}
                                {displayData.quote && (
                                    <div className="relative z-10 bg-gradient-to-t from-slate-900/95 via-slate-900/70 to-transparent p-6 sm:p-8 lg:p-10 pointer-events-none">
                                        <p className="text-white text-lg md:text-xl lg:text-2xl font-medium leading-snug drop-shadow-md">
                                            {displayData.quote}
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        </div>
                    </div>

                </div>

                {/* Additional Description for Standalone Page */}
                {isStandalone && displayData.mainPageDescription && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="mt-20 lg:mt-28 relative"
                    >
                        {/* Decorative quotes abstract */}
                        <div className="absolute -top-10 lg:-top-16 left-4 lg:left-10 text-[100px] lg:text-[140px] text-pink-50 font-serif leading-none select-none z-0">
                            &ldquo;
                        </div>

                        <div className="relative z-10 bg-brand-card rounded-3xl p-8 md:p-12 lg:p-16 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] border border-slate-100 overflow-hidden group hover:border-[#E91E63]/20 transition-colors duration-500">
                            {/* Inner gradient line */}
                            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#E91E63] via-pink-400 to-[#E91E63] opacity-80"></div>

                            <div className="max-w-4xl mx-auto">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-[2px] bg-brand-gold"></div>
                                    <span className="text-sm font-semibold tracking-wider text-brand-gold uppercase">Overview</span>
                                </div>
                                <div className="text-lg md:text-xl text-brand-textLight leading-loose whitespace-pre-wrap font-light">
                                    {displayData.mainPageDescription}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default CareerChavdiSection;
